import { useEffect, useRef, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { AlertTriangle, CheckCircle2, Copy, ExternalLink, Eye, RotateCw } from 'lucide-react';
import { previewBrPdf, previewBrEmail, previewSnklPdf, requestBr, previewAsClientBr } from '@/services/deals.service.js';
import { cn } from '@/lib/utils.js';
import { toast } from '@/components/ui/toaster.jsx';
import PdfPreviewFrame from './PdfPreviewFrame.jsx';
import { formatDateTime } from '@/lib/date-display.js';

// BrRequestDialog - broker-facing preview-before-send for the Builders
// Risk signing request. BR-AND-SNKL-TOWER-SPEC.md preview pattern,
// mirrors LPRSheet but simpler: BR data is already locked in CRM, so the
// dialog is review-only (no inline form). Render once on open, refetch
// on Regenerate.
//
// Three things the broker confirms before sending:
//   1. Recipient (To: line) - resolved from Deal.Contact_Name
//   2. Application PDF (PdfPreviewFrame) - what the insured will sign
//   3. Email body (iframe) - the message containing the signing link
//
// Send fires the existing /request endpoint (mints token, sends email,
// writes BR_Status=requested). After send, success state shows the link
// in case the broker wants to copy it to a backup channel.
//
// No em dashes anywhere (CLAUDE.md hard rule).

export default function BrRequestDialog({ dealId, open, onOpenChange, onChange }) {
  const [recipient, setRecipient] = useState(null);
  const [subject, setSubject] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [snklPdfUrl, setSnklPdfUrl] = useState(null);
  const [emailHtml, setEmailHtml] = useState(null);
  const [tab, setTab] = useState('pdf');
  const [loading, setLoading] = useState(false);
  const [snklLoading, setSnklLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  // includeSnkl bundles a Statement of No Known Losses into the same signing
  // session. The default is an OBSERVATION of this client's last placement
  // first and the carrier default only second; the server resolves it and
  // returns snkl_suggestion { checked, source, reason, carrier_changed }.
  // suggestionApplied tracks whether we have already seeded it, so the
  // broker's manual toggle is not overwritten on Regenerate.
  const [includeSnkl, setIncludeSnkl] = useState(false);
  const [snklSuggestion, setSnklSuggestion] = useState(null);
  const [suggestionApplied, setSuggestionApplied] = useState(false);
  // Ship 2d preview-as-client button state (handler defined below).
  const [previewing, setPreviewing] = useState(false);
  const pdfUrlRef = useRef(null);
  const snklPdfUrlRef = useRef(null);

  async function loadSnklPreview() {
    if (!dealId) return;
    setSnklLoading(true);
    try {
      const blob = await previewSnklPdf(dealId);
      const newUrl = URL.createObjectURL(blob);
      const prev = snklPdfUrlRef.current;
      snklPdfUrlRef.current = newUrl;
      setSnklPdfUrl(newUrl);
      if (prev) URL.revokeObjectURL(prev);
    } catch (e) {
      // SNKL preview failure does NOT block sending - the backend will
      // attempt the SNKL render server-side at bind-sign time and emit
      // a degraded snkl:{success:false} response if it still fails.
      // Send button stays enabled as long as recipient is resolved.
      setError(`SNKL preview render did not complete (${e?.message || 'unknown error'}). You can still click Send - the SNKL letter will attempt to bundle server-side at sign time. If that also fails, you can re-request SNKL separately afterward.`);
    } finally {
      setSnklLoading(false);
    }
  }

  // Promise.allSettled (not Promise.all) so a PDF render failure does
  // not prevent the email preview (and therefore the recipient) from
  // resolving. Send stays enabled on recipient.toEmail; broker can ship
  // even when BR PDF previewing has a transient hiccup.
  async function loadPreview() {
    if (!dealId) return;
    setLoading(true); setError(null);
    try {
      const [pdfResult, emailResult] = await Promise.allSettled([
        previewBrPdf(dealId),
        previewBrEmail(dealId),
      ]);
      if (emailResult.status === 'fulfilled') {
        const emailRes = emailResult.value;
        setEmailHtml(emailRes?.html || null);
        setRecipient(emailRes?.recipient || null);
        setSubject(emailRes?.subject || '');
        // Seed the SNKL checkbox once. After the first apply the broker owns
        // the toggle and Regenerate must not re-suggest (otherwise it clobbers
        // a deliberate uncheck).
        //
        // THE REASON LINE IS THE GATE. A box pre-checked with no visible cause
        // is the exact failure this replaced: a default that is right until it
        // is wrong and never says which. So the box is only pre-checked when a
        // reason came back to render beside it. If the reason line is ever
        // removed from the markup below, delete this seeding with it and let
        // the box start unchecked. (CC PROMPT 46 Part D.)
        const sug = emailRes?.snkl_suggestion || null;
        if (sug?.reason) setSnklSuggestion(sug);
        if (!suggestionApplied && sug?.checked === true && sug?.reason) {
          setIncludeSnkl(true);
        }
        setSuggestionApplied(true);
      }
      if (pdfResult.status === 'fulfilled') {
        const newUrl = URL.createObjectURL(pdfResult.value);
        const prev = pdfUrlRef.current;
        pdfUrlRef.current = newUrl;
        setPdfUrl(newUrl);
        if (prev) URL.revokeObjectURL(prev);
      }
      const failures = [];
      if (pdfResult.status === 'rejected') {
        failures.push(`BR PDF preview did not load (${pdfResult.reason?.message || 'unknown'}).`);
      }
      if (emailResult.status === 'rejected') {
        failures.push(`Email preview did not load (${emailResult.reason?.message || 'unknown'}).`);
      }
      if (failures.length) {
        const sendable = emailResult.status === 'fulfilled' && emailResult.value?.recipient?.toEmail;
        setError(sendable
          ? `${failures.join(' ')} You can still click Send - the missing piece will retry server-side.`
          : failures.join(' '));
      }
    } finally {
      setLoading(false);
    }
  }

  // Load once when the sheet opens.
  useEffect(() => {
    if (open && dealId && !done) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dealId]);

  // Fetch the SNKL preview when the broker toggles the bundle ON. The
  // initial-load loadPreview() does NOT fetch SNKL (only BR + email);
  // this effect handles the lazy-load. When the broker toggles OFF, no
  // refetch - just hide the tab and let the URL ref linger until close.
  useEffect(() => {
    if (open && includeSnkl && !snklPdfUrl && !snklLoading) {
      loadSnklPreview();
    }
    // If the broker unchecks SNKL while on the SNKL tab, swing back to PDF.
    if (!includeSnkl && tab === 'snkl') setTab('pdf');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, includeSnkl]);

  // Reset on close (deferred so the broker does not watch fields blank
  // out during the sheet exit animation, same trick as LPRSheet).
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setRecipient(null); setSubject(''); setPdfUrl(null); setSnklPdfUrl(null);
        setEmailHtml(null); setTab('pdf'); setError(null); setDone(null);
        setIncludeSnkl(false); setSuggestionApplied(false);
        if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; }
        if (snklPdfUrlRef.current) { URL.revokeObjectURL(snklPdfUrlRef.current); snklPdfUrlRef.current = null; }
      }, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Final cleanup on unmount.
  useEffect(() => () => {
    if (pdfUrlRef.current) { URL.revokeObjectURL(pdfUrlRef.current); pdfUrlRef.current = null; }
    if (snklPdfUrlRef.current) { URL.revokeObjectURL(snklPdfUrlRef.current); snklPdfUrlRef.current = null; }
  }, []);

  async function handleSend() {
    if (!dealId || sending) return;
    setSending(true); setError(null);
    try {
      const res = await requestBr(dealId, { includeSnkl });
      if (!res?.success) throw new Error(res?.error || 'Send failed');
      setDone(res);
      for (const w of res.warnings || []) toast.info(w);
      onChange?.();
    } catch (e) {
      setError(e?.message || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  // Ship 2d preview-as-client: mint a preview-flagged token carrying the
  // same SNKL bundle the real send would and open the Tower wizard in a
  // new tab. No email goes out, no BR_Status write happens, and the token
  // can never sign (server 403s it).
  async function handlePreviewAsClient() {
    if (!dealId || previewing) return;
    setPreviewing(true); setError(null);
    try {
      const res = await previewAsClientBr(dealId, { includeSnkl });
      if (!res?.success || !res?.link) throw new Error(res?.error || 'Preview failed');
      window.open(res.link, '_blank', 'noopener');
    } catch (e) {
      setError(e?.body?.error || e?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  }

  function copyLink() {
    if (!done?.link || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(done.link).catch(() => {});
  }

  const canSend = !sending && !loading && !!recipient?.toEmail;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex h-[96dvh] flex-col overflow-hidden p-0 md:mx-auto md:max-w-5xl"
      >
        {/* h-[96dvh], not max-h: the preview below is flex-1 and an iframe
            only fills a parent that has a height. With max-h alone the
            viewer rendered as a 280px letterboxed strip (2026-09-30). p-0
            because every section carries its own gutter. */}
        <SheetHeader className="px-4 pb-3 pr-14 pt-4">
          <SheetTitle>Builders Risk binding signature request</SheetTitle>
          <SheetDescription>
            Review what the insured will receive. Nothing is sent until you click Send.
          </SheetDescription>
        </SheetHeader>

        {/* Recipient confirmation strip */}
        <div className="border-y border-border bg-surface px-4 py-2 text-sm">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">To</span>
          <span className="ml-2">
            {recipient?.toEmail ? (
              <>
                <span className="font-medium text-text">{recipient.toName || ''}</span>
                <span className="text-text-muted"> &lt;{recipient.toEmail}&gt;</span>
              </>
            ) : (
              <span className="text-text-muted">{loading ? 'Resolving recipient...' : 'No email on the Deal Contact'}</span>
            )}
          </span>
          {subject ? (
            <>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">Subject</div>
              <div className="text-xs text-text">{subject}</div>
            </>
          ) : null}
        </div>

        {/* SNKL bundle toggle. When checked, the signing token carries
            bundle_docs:['snkl'] and the insured signs both the BR application
            AND the SNKL letter in one Tower session, two PDFs filing on submit.

            THE REASON LINE BELOW IS LOAD-BEARING, NOT DECORATION. The default
            is only ever pre-checked because a reason came back to show next to
            it. If this line is removed, remove the pre-check seeding in the
            effect above too: an unexplained default is what produced the
            inverted carrier flag in the first place. */}
        <div className="border-b border-border bg-surface px-4 py-2">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeSnkl}
              onChange={(e) => setIncludeSnkl(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border bg-bg accent-accent"
            />
            <span>
              <span className="font-medium text-text">Require a Statement of No Known Losses</span>
              {/* THE REASON LINE. It names which of the three steps the default
                  came from, every time, so the box never sits checked with an
                  invisible cause. carrier_changed gets its own emphasis: a
                  client who moved from ABC to PAU may not need the same
                  document, and the broker is the one who knows. */}
              {snklSuggestion?.reason ? (
                <span
                  className={`mt-0.5 block text-xs ${snklSuggestion.carrier_changed ? 'font-medium text-warning' : 'text-text-muted'}`}
                >
                  {includeSnkl !== snklSuggestion.checked ? 'Overridden by you. Default was ' : ''}
                  {snklSuggestion.reason}
                </span>
              ) : (
                <span className="mt-0.5 block text-xs text-text-muted">
                  No default on file, so nothing is pre-selected.
                </span>
              )}
            </span>
          </label>
        </div>

        {done ? (
          <SuccessBody done={done} onCopy={copyLink} />
        ) : (
          <>
            {/* Tabs + Regenerate */}
            <div className="flex items-center border-b border-border bg-surface px-2">
              <TabBtn active={tab === 'pdf'} onClick={() => setTab('pdf')}>Application PDF</TabBtn>
              {includeSnkl ? (
                <TabBtn active={tab === 'snkl'} onClick={() => setTab('snkl')}>SNKL letter</TabBtn>
              ) : null}
              <TabBtn active={tab === 'email'} onClick={() => setTab('email')}>Email preview</TabBtn>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { loadPreview(); if (includeSnkl) loadSnklPreview(); }}
                disabled={loading || snklLoading}
                className="gap-1 text-xs text-text-muted hover:text-text"
              >
                <RotateCw size={12} strokeWidth={1.75} className={cn((loading || snklLoading) && 'animate-spin')} />
                {loading || snklLoading ? 'Loading...' : 'Regenerate'}
              </Button>
            </div>

            {/* Tab content. flex-1 + min-h-0 takes every row the sheet's
                set height leaves after the strips and footer; the PDF
                frames fill it on desktop and hand the phone an open-in-tab
                door instead (PdfPreviewFrame). */}
            <div className="flex-1 min-h-0 bg-white">
              {tab === 'pdf' ? (
                pdfUrl ? (
                  <PdfPreviewFrame url={pdfUrl} title="BR application preview" openLabel="Open application PDF" />
                ) : (
                  <PreviewLoader label={loading ? 'Rendering application PDF...' : 'PDF will appear here.'} />
                )
              ) : null}
              {tab === 'snkl' && includeSnkl ? (
                snklPdfUrl ? (
                  <PdfPreviewFrame url={snklPdfUrl} title="SNKL letter preview" openLabel="Open SNKL letter" />
                ) : (
                  <PreviewLoader label={snklLoading ? 'Rendering SNKL letter...' : 'SNKL letter will appear here.'} />
                )
              ) : null}
              {tab === 'email' ? (
                emailHtml ? (
                  <iframe
                    srcDoc={emailHtml}
                    title="BR request email preview"
                    className="h-full w-full"
                    style={{ border: 'none', background: 'white' }}
                  />
                ) : (
                  <PreviewLoader label={loading ? 'Rendering email preview...' : 'Email body will appear here.'} />
                )
              ) : null}
            </div>

            {tab === 'email' ? (
              <div className="border-t border-border bg-surface px-4 py-2 text-[11px] text-text-muted">
                The CTA link in the body shows a placeholder. A real signing token is generated when you click Send.
              </div>
            ) : null}
          </>
        )}

        {error ? (
          <div className="mx-4 my-2 flex items-start gap-2 rounded-card border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {done ? null : (
          <SheetFooter className="border-t border-border bg-surface px-4 py-3">
            <Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handlePreviewAsClient}
              disabled={previewing || sending}
              className="gap-1.5"
              title="Walk the exact Tower wizard the insured will see. Nothing can be signed, sent, or charged from a preview."
            >
              <Eye size={14} strokeWidth={1.75} />
              {previewing ? 'Opening preview...' : 'Preview as client'}
            </Button>
            <Button
              onClick={handleSend}
              disabled={!canSend}
              className="bg-accent text-white hover:bg-accent/90"
              title={recipient?.toEmail ? '' : 'Set a Contact email on the Deal before sending.'}
            >
              {sending ? 'Sending...' : 'Send for signature'}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-2 text-xs font-medium border-b-2 transition-colors',
        active
          ? 'border-accent text-text'
          : 'border-transparent text-text-muted hover:text-text'
      )}
    >
      {children}
    </button>
  );
}

function PreviewLoader({ label }) {
  return (
    <div className="flex h-full min-h-[240px] items-center justify-center text-sm text-text-muted">
      {label}
    </div>
  );
}

function SuccessBody({ done, onCopy }) {
  return (
    <div className="flex-1 overflow-y-auto px-4 py-6">
      <div className="mx-auto max-w-xl">
        <div className="rounded-card border border-success bg-success-soft p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-success">
            <CheckCircle2 size={16} strokeWidth={1.75} />
            <span>Signature request sent</span>
          </div>
          <p className="mt-2 text-xs text-text-muted">
            {done.emailed_to ? (
              <>Emailed to <span className="text-text">{done.emailed_to}</span>. </>
            ) : 'Token minted but no recipient email on file. '}
            The insured can sign at the Tower link below.
          </p>
        </div>

        {done.link ? (
          <div className="mt-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">Tower link</div>
            <div className="mt-1 flex items-center gap-2">
              <a
                href={done.link}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-info hover:underline"
              >
                {done.link}
              </a>
              <button
                type="button"
                onClick={onCopy}
                className="rounded-card border border-border bg-surface p-1 text-text-muted hover:text-text"
                aria-label="Copy link"
              >
                <Copy size={12} strokeWidth={1.75} />
              </button>
              <a
                href={done.link}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-card border border-border bg-surface p-1 text-text-muted hover:text-text"
                aria-label="Open link"
              >
                <ExternalLink size={12} strokeWidth={1.75} />
              </a>
            </div>
          </div>
        ) : null}

        {done.expires_at ? (
          <div className="mt-3 text-[11px] text-text-dim">
            Tower link expires {formatDateTime(done.expires_at)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
